import { z } from 'zod';

export const groupPermissionDtoSchema = z.object({
  funcGroupDetailId: z.string().nullable(),
  funcId: z.string().nullable(),
  funcCode: z.string().nullable(),
  funcName: z.string().nullable(),
  funcQueryFlag: z.boolean().nullable(),
  funcEditFlag: z.boolean().nullable(),
  funcDeleteFlag: z.boolean().nullable(),
  lockVersion: z.number().nullable()
});
export type GroupPermissionDto = z.infer<typeof groupPermissionDtoSchema>;

export const permissionDtoSchema = z.object({
  funcId: z.string().nullable(),
  funcCode: z.string().nullable(),
  funcName: z.string().nullable(),
  funcQueryFlag: z.boolean().nullable(),
  funcEditFlag: z.boolean().nullable(),
  funcDeleteFlag: z.boolean().nullable()
});
export type PermissionDto = z.infer<typeof permissionDtoSchema>;

export const permissionGroupDtoSchema = z.object({
  funcGroupId: z.string().nullable(),
  groupName: z.string().nullable(),
  description: z.string().nullable(),
  activeFlag: z.boolean().nullable(),
  permissionCount: z.number().nullable(),
  modifyUser: z.string().nullable(),
  modifyUserName: z.string().nullable(),
  modifyDatetime: z.string().nullable(),
  lockVersion: z.number().nullable()
});
export type PermissionGroupDto = z.infer<typeof permissionGroupDtoSchema>;

export const userListByPermissionGroupDtoSchema = z.object({
  userPermissionGroupId: z.string().nullable(),
  userId: z.string().nullable(),
  userName: z.string().nullable(),
  userEmail: z.string().nullable(),
  empNo: z.string().nullable(),
  practitionerTypeCode: z.enum(["doctor", "nurse"]).nullable(),
  practitionerTypeName: z.string().nullable(),
  userStatus: z.string().nullable(),
  lastLoginDatetime: z.string().nullable(),
  superAdminFlag: z.boolean().nullable(),
  userTenantLockVersion: z.number().nullable()
});
export type UserListByPermissionGroupDto = z.infer<typeof userListByPermissionGroupDtoSchema>;

export const groupAssignedUserDtoSchema = z.object({
  userPermissionGroupId: z.string().nullable(),
  userId: z.string().nullable(),
  userName: z.string().nullable(),
  userEmail: z.string().nullable(),
  userEmp: z.string().nullable(),
  practitionerTypeCode: z.string().nullable(),
  practitionerTypeName: z.string().nullable(),
  userStatus: z.string().nullable(),
  effectiveDate: z.string().nullable(),
  expiryDate: z.string().nullable(),
  effectiveStatus: z.enum(["EFFECTIVE", "PENDING", "EXPIRED", "DISABLED"]).nullable(),
  createUser: z.string().nullable(),
  createUserName: z.string().nullable(),
  createDatetime: z.string().nullable(),
  lockVersion: z.number().nullable()
});
export type GroupAssignedUserDto = z.infer<typeof groupAssignedUserDtoSchema>;

export const permissionAssignmentDtoSchema = z.object({
  userPermissionFuncId: z.string().nullable(),
  funcId: z.string().nullable(),
  funcCode: z.string().nullable(),
  funcName: z.string().nullable(),
  funcQueryFlag: z.boolean().nullable(),
  queryEffectiveDate: z.string().nullable(),
  queryExpiryDate: z.string().nullable(),
  funcEditFlag: z.boolean().nullable(),
  editEffectiveDate: z.string().nullable(),
  editExpiryDate: z.string().nullable(),
  funcDeleteFlag: z.boolean().nullable(),
  deleteEffectiveDate: z.string().nullable(),
  deleteExpiryDate: z.string().nullable(),
  effectiveStatus: z.enum(["EFFECTIVE", "PENDING", "EXPIRED", "DISABLED"]).nullable(),
  lockVersion: z.number().nullable()
});
export type PermissionAssignmentDto = z.infer<typeof permissionAssignmentDtoSchema>;

export const groupAssignmentDtoSchema = z.object({
  userPermissionGroupId: z.string().nullable(),
  funcGroupId: z.string().nullable(),
  groupName: z.string().nullable(),
  description: z.string().nullable(),
  groupActiveFlag: z.boolean().nullable(),
  effectiveDate: z.string().nullable(),
  expiryDate: z.string().nullable(),
  effectiveStatus: z.enum(["EFFECTIVE", "PENDING", "EXPIRED", "DISABLED"]).nullable(),
  lockVersion: z.number().nullable(),
  permissionList: z.array(groupPermissionDtoSchema).nullable()
});
export type GroupAssignmentDto = z.infer<typeof groupAssignmentDtoSchema>;

export const tenantItemSchema = z.object({
  id: z.string().nullable(),
  tenantCode: z.string().nullable(),
  tenantName: z.string().nullable()
});
export type TenantItem = z.infer<typeof tenantItemSchema>;

export const failedDetailDtoSchema = z.object({
  userId: z.string().nullable(),
  userName: z.string().nullable(),
  userEmp: z.string().nullable(),
  practitionerTypeCode: z.string().nullable(),
  practitionerTypeName: z.string().nullable(),
  userEmail: z.string().nullable(),
  assignType: z.enum(["GROUP", "FUNC"]).nullable(),
  targetId: z.string().nullable(),
  targetName: z.string().nullable(),
  funcQueryFlag: z.boolean().nullable(),
  funcEditFlag: z.boolean().nullable(),
  funcDeleteFlag: z.boolean().nullable(),
  failedReason: z.enum(["ACCOUNT_NOT_ACTIVE", "ACCOUNT_NOT_FOUND", "GROUP_NOT_AVAILABLE", "PERMIT_NOT_AVAILABLE", "OTHER"]).nullable(),
  failedReasonI18n: z.string().nullable()
});
export type FailedDetailDto = z.infer<typeof failedDetailDtoSchema>;

export const userPermissionResSchema = z.object({
  apiUrl: z.string().nullable(),
  httpMethod: z.string().nullable()
});
export type UserPermissionRes = z.infer<typeof userPermissionResSchema>;

export const availablePermissionResSchema = z.object({
  funcGroupId: z.string().nullable(),
  groupName: z.string().nullable(),
  description: z.string().nullable(),
  lockVersion: z.number().nullable(),
  permissionList: z.array(groupPermissionDtoSchema).nullable()
});
export type AvailablePermissionRes = z.infer<typeof availablePermissionResSchema>;

export const permissionGroupDetailResSchema = z.object({
  funcGroupId: z.string().nullable(),
  groupName: z.string().nullable(),
  description: z.string().nullable(),
  activeFlag: z.boolean().nullable(),
  permissionList: z.array(permissionDtoSchema).nullable(),
  modifyUser: z.string().nullable(),
  modifyUserName: z.string().nullable(),
  modifyDatetime: z.string().nullable(),
  lockVersion: z.number().nullable(),
  canDisable: z.boolean().nullable(),
  canActive: z.boolean().nullable(),
  canDelete: z.boolean().nullable()
});
export type PermissionGroupDetailRes = z.infer<typeof permissionGroupDetailResSchema>;

export const permissionGroupListResSchema = z.object({
  permissionGroupList: z.array(permissionGroupDtoSchema).nullable(),
  totalElements: z.number().nullable()
});
export type PermissionGroupListRes = z.infer<typeof permissionGroupListResSchema>;

export const queryUserListByPermissionGroupResSchema = z.object({
  userList: z.array(userListByPermissionGroupDtoSchema).nullable(),
  page: z.number().nullable(),
  size: z.number().nullable(),
  totalElements: z.number().nullable(),
  totalPages: z.number().nullable()
});
export type QueryUserListByPermissionGroupRes = z.infer<typeof queryUserListByPermissionGroupResSchema>;

export const availablePermissionItemResSchema = z.object({
  funcId: z.string().nullable(),
  funcCode: z.string().nullable(),
  funcName: z.string().nullable(),
  funcQueryFlag: z.boolean().nullable(),
  funcEditFlag: z.boolean().nullable(),
  funcDeleteFlag: z.boolean().nullable()
});
export type AvailablePermissionItemRes = z.infer<typeof availablePermissionItemResSchema>;

export const effectivePermissionResSchema = z.object({
  funcId: z.string().nullable(),
  funcCode: z.string().nullable(),
  funcName: z.string().nullable(),
  funcQueryFlag: z.boolean().nullable(),
  queryEffectiveDate: z.string().nullable(),
  queryExpiryDate: z.string().nullable(),
  funcEditFlag: z.boolean().nullable(),
  editEffectiveDate: z.string().nullable(),
  editExpiryDate: z.string().nullable(),
  funcDeleteFlag: z.boolean().nullable(),
  deleteEffectiveDate: z.string().nullable(),
  deleteExpiryDate: z.string().nullable()
});
export type EffectivePermissionRes = z.infer<typeof effectivePermissionResSchema>;

export const groupAssignedUserListResSchema = z.object({
  funcGroupId: z.string().nullable(),
  groupName: z.string().nullable(),
  description: z.string().nullable(),
  activeFlag: z.boolean().nullable(),
  assignedUserList: z.array(groupAssignedUserDtoSchema).nullable(),
  totalElements: z.number().nullable()
});
export type GroupAssignedUserListRes = z.infer<typeof groupAssignedUserListResSchema>;

export const userPermissionDetailResSchema = z.object({
  userId: z.string().nullable(),
  userName: z.string().nullable(),
  userEmail: z.string().nullable(),
  practitionerTypeCode: z.string().nullable(),
  practitionerTypeName: z.string().nullable(),
  userStatus: z.string().nullable(),
  groupAssignmentList: z.array(groupAssignmentDtoSchema).nullable(),
  permissionAssignmentList: z.array(permissionAssignmentDtoSchema).nullable()
});
export type UserPermissionDetailRes = z.infer<typeof userPermissionDetailResSchema>;

export const hisIntegrationLoginResSchema = z.object({
  tenantId: z.string().nullable(),
  id: z.string().nullable(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  roles: z.array(z.string()).nullable(),
  funcCodes: z.array(z.string()).nullable(),
  apiPermissions: z.array(z.string()).nullable(),
  token: z.string().nullable(),
  refreshToken: z.string().nullable()
});
export type HisIntegrationLoginRes = z.infer<typeof hisIntegrationLoginResSchema>;

export const loginStep1ResSchema = z.object({
  id: z.string().nullable(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  passwordExpired: z.boolean().nullable(),
  tenantList: z.array(tenantItemSchema).nullable()
});
export type LoginStep1Res = z.infer<typeof loginStep1ResSchema>;

export const loginStep2ResSchema = z.object({
  tenantId: z.string().nullable(),
  id: z.string().nullable(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  roles: z.array(z.string()).nullable(),
  funcCodes: z.array(z.string()).nullable(),
  apiPermissions: z.array(z.string()).nullable(),
  token: z.string().nullable(),
  refreshToken: z.string().nullable()
});
export type LoginStep2Res = z.infer<typeof loginStep2ResSchema>;

export const refreshTokenResSchema = z.object({
  token: z.string().nullable(),
  refreshToken: z.string().nullable()
});
export type RefreshTokenRes = z.infer<typeof refreshTokenResSchema>;

export const addPermissionGroupResSchema = z.object({
  funcGroupId: z.string().nullable()
});
export type AddPermissionGroupRes = z.infer<typeof addPermissionGroupResSchema>;

export const addBatchUserPermissionAssignResSchema = z.object({
  batchId: z.string().nullable(),
  successCount: z.number().nullable(),
  failedCount: z.number().nullable(),
  skipCount: z.number().nullable(),
  failedDetails: z.array(failedDetailDtoSchema).nullable()
});
export type AddBatchUserPermissionAssignRes = z.infer<typeof addBatchUserPermissionAssignResSchema>;

export const permissionAssignDtoSchema = z.object({
  funcId: z.string().min(1),
  funcQueryFlag: z.boolean().nullable(),
  funcEditFlag: z.boolean().nullable(),
  funcDeleteFlag: z.boolean().nullable(),
  lockVersion: z.number().nullable()
});
export type PermissionAssignDto = z.infer<typeof permissionAssignDtoSchema>;

export const groupAssignReqDtoSchema = z.object({
  funcGroupId: z.string().min(1),
  effectiveDate: z.string().nullable(),
  expiryDate: z.string().nullable()
});
export type GroupAssignReqDto = z.infer<typeof groupAssignReqDtoSchema>;

export const permissionAssignReqDtoSchema = z.object({
  funcId: z.string().min(1),
  funcQueryFlag: z.boolean().nullable(),
  queryEffectiveDate: z.string().nullable(),
  queryExpiryDate: z.string().nullable(),
  funcEditFlag: z.boolean().nullable(),
  editEffectiveDate: z.string().nullable(),
  editExpiryDate: z.string().nullable(),
  funcDeleteFlag: z.boolean().nullable(),
  deleteEffectiveDate: z.string().nullable(),
  deleteExpiryDate: z.string().nullable()
});
export type PermissionAssignReqDto = z.infer<typeof permissionAssignReqDtoSchema>;

export const updateUserPermissionFuncResSchema = z.object({
  userPermissionFuncId: z.string().min(1),
  funcQueryFlag: z.boolean().nullable(),
  funcEditFlag: z.boolean().nullable(),
  funcDeleteFlag: z.boolean().nullable(),
  funcAllFlag: z.boolean().nullable(),
  lockVersion: z.number()
});
export type UpdateUserPermissionFuncRes = z.infer<typeof updateUserPermissionFuncResSchema>;

export const apiResponseListUserPermissionResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(userPermissionResSchema).nullable()
});
export type ApiResponseListUserPermissionRes = z.infer<typeof apiResponseListUserPermissionResSchema>;

export const apiPageResponseObjectSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.unknown().nullable(),
  totalPageSize: z.number().nullable(),
  totalItemSize: z.number().nullable(),
  currentPageItemSize: z.number().nullable(),
  pageSize: z.number().nullable()
});
export type ApiPageResponseObject = z.infer<typeof apiPageResponseObjectSchema>;

export const apiResponseAvailablePermissionResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: availablePermissionResSchema.nullable()
});
export type ApiResponseAvailablePermissionRes = z.infer<typeof apiResponseAvailablePermissionResSchema>;

export const apiResponsePermissionGroupDetailResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: permissionGroupDetailResSchema.nullable()
});
export type ApiResponsePermissionGroupDetailRes = z.infer<typeof apiResponsePermissionGroupDetailResSchema>;

export const apiResponsePermissionGroupListResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: permissionGroupListResSchema.nullable()
});
export type ApiResponsePermissionGroupListRes = z.infer<typeof apiResponsePermissionGroupListResSchema>;

export const apiResponseQueryUserListByPermissionGroupResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryUserListByPermissionGroupResSchema.nullable()
});
export type ApiResponseQueryUserListByPermissionGroupRes = z.infer<typeof apiResponseQueryUserListByPermissionGroupResSchema>;

export const apiResponseListAvailablePermissionItemResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(availablePermissionItemResSchema).nullable()
});
export type ApiResponseListAvailablePermissionItemRes = z.infer<typeof apiResponseListAvailablePermissionItemResSchema>;

export const apiResponseListEffectivePermissionResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(effectivePermissionResSchema).nullable()
});
export type ApiResponseListEffectivePermissionRes = z.infer<typeof apiResponseListEffectivePermissionResSchema>;

export const apiResponseGroupAssignedUserListResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: groupAssignedUserListResSchema.nullable()
});
export type ApiResponseGroupAssignedUserListRes = z.infer<typeof apiResponseGroupAssignedUserListResSchema>;

export const apiResponseUserPermissionDetailResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: userPermissionDetailResSchema.nullable()
});
export type ApiResponseUserPermissionDetailRes = z.infer<typeof apiResponseUserPermissionDetailResSchema>;

export const apiResponseHisIntegrationLoginResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: hisIntegrationLoginResSchema.nullable()
});
export type ApiResponseHisIntegrationLoginRes = z.infer<typeof apiResponseHisIntegrationLoginResSchema>;

export const hisIntegrationLoginReqSchema = z.object({
  loginCode: z.string().min(1),
  password: z.string().min(1),
  userEmail: z.string().min(1)
});
export type HisIntegrationLoginReq = z.infer<typeof hisIntegrationLoginReqSchema>;

export const apiResponseLoginStep1ResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: loginStep1ResSchema.nullable()
});
export type ApiResponseLoginStep1Res = z.infer<typeof apiResponseLoginStep1ResSchema>;

export const loginStep1ReqSchema = z.object({
  email: z.string().min(1),
  password: z.string().min(1)
});
export type LoginStep1Req = z.infer<typeof loginStep1ReqSchema>;

export const apiResponseLoginStep2ResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: loginStep2ResSchema.nullable()
});
export type ApiResponseLoginStep2Res = z.infer<typeof apiResponseLoginStep2ResSchema>;

export const loginStep2ReqSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string().min(1),
  email: z.string().min(1),
  password: z.string().min(1)
});
export type LoginStep2Req = z.infer<typeof loginStep2ReqSchema>;

export const apiResponseRefreshTokenResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: refreshTokenResSchema.nullable()
});
export type ApiResponseRefreshTokenRes = z.infer<typeof apiResponseRefreshTokenResSchema>;

export const refreshTokenReqSchema = z.object({
  token: z.string().min(1),
  refreshToken: z.string().min(1)
});
export type RefreshTokenReq = z.infer<typeof refreshTokenReqSchema>;

export const apiResponseObjectSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.unknown().nullable()
});
export type ApiResponseObject = z.infer<typeof apiResponseObjectSchema>;

export const reqCrudSaveSchema = z.object({
  systemNo: z.string().min(1),
  tableName: z.string().min(1),
  tableData: z.preprocess((val) => val === undefined ? null : val, z.array(z.unknown()).nullish())
});
export type ReqCrudSave = z.infer<typeof reqCrudSaveSchema>;

export const apiResponseAddPermissionGroupResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: addPermissionGroupResSchema.nullable()
});
export type ApiResponseAddPermissionGroupRes = z.infer<typeof apiResponseAddPermissionGroupResSchema>;

export const addPermissionGroupReqSchema = z.object({
  groupName: z.string().min(1),
  description: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish())
});
export type AddPermissionGroupReq = z.infer<typeof addPermissionGroupReqSchema>;

export const apiResponseAddBatchUserPermissionAssignResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: addBatchUserPermissionAssignResSchema.nullable()
});
export type ApiResponseAddBatchUserPermissionAssignRes = z.infer<typeof apiResponseAddBatchUserPermissionAssignResSchema>;

export const addBatchUserPermissionAssignReqSchema = z.object({
  funcGroupIds: z.preprocess((val) => val === undefined ? null : val, z.array(z.string()).nullish()),
  permissionList: z.preprocess((val) => val === undefined ? null : val, z.array(permissionAssignDtoSchema).nullish()),
  userIds: z.array(z.string()),
  effectiveDate: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  expiryDate: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  batchRemark: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish())
});
export type AddBatchUserPermissionAssignReq = z.infer<typeof addBatchUserPermissionAssignReqSchema>;

export const addUserGroupAssignReqSchema = z.object({
  userId: z.string().min(1),
  groupAssignmentList: z.array(groupAssignReqDtoSchema)
});
export type AddUserGroupAssignReq = z.infer<typeof addUserGroupAssignReqSchema>;

export const addUserPermissionAssignReqSchema = z.object({
  userId: z.string().min(1),
  permissionAssignmentList: z.array(permissionAssignReqDtoSchema)
});
export type AddUserPermissionAssignReq = z.infer<typeof addUserPermissionAssignReqSchema>;

export const apiResponseUpdateUserPermissionFuncResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: updateUserPermissionFuncResSchema.nullable()
});
export type ApiResponseUpdateUserPermissionFuncRes = z.infer<typeof apiResponseUpdateUserPermissionFuncResSchema>;

export const updateUserPermissionFuncReqSchema = z.object({
  userPermissionFuncId: z.string().min(1),
  funcQueryFlag: z.preprocess((val) => val === undefined ? null : val, z.boolean().nullish()),
  funcEditFlag: z.preprocess((val) => val === undefined ? null : val, z.boolean().nullish()),
  funcDeleteFlag: z.preprocess((val) => val === undefined ? null : val, z.boolean().nullish()),
  funcAllFlag: z.preprocess((val) => val === undefined ? null : val, z.boolean().nullish()),
  lockVersion: z.number()
});
export type UpdateUserPermissionFuncReq = z.infer<typeof updateUserPermissionFuncReqSchema>;

export const activePermissionGroupReqSchema = z.object({
  funcGroupId: z.string().min(1),
  lockVersion: z.number()
});
export type ActivePermissionGroupReq = z.infer<typeof activePermissionGroupReqSchema>;

export const disablePermissionGroupReqSchema = z.object({
  funcGroupId: z.string().min(1),
  lockVersion: z.number()
});
export type DisablePermissionGroupReq = z.infer<typeof disablePermissionGroupReqSchema>;

export const updatePermissionGroupReqSchema = z.object({
  funcGroupId: z.string().min(1),
  groupName: z.string().min(1),
  description: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  permissionList: z.preprocess((val) => val === undefined ? null : val, z.array(permissionAssignDtoSchema).nullish()),
  lockVersion: z.number()
});
export type UpdatePermissionGroupReq = z.infer<typeof updatePermissionGroupReqSchema>;

export const apiResponseVoidSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.unknown().nullable()
});
export type ApiResponseVoid = z.infer<typeof apiResponseVoidSchema>;

export const updateUserPermissionTimeRangeReqSchema = z.object({
  userPermissionGroupId: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  userPermissionFuncId: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  effectiveDate: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  expiryDate: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  queryEffectiveDate: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  queryExpiryDate: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  editEffectiveDate: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  editExpiryDate: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  deleteEffectiveDate: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  deleteExpiryDate: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  lockVersion: z.number()
});
export type UpdateUserPermissionTimeRangeReq = z.infer<typeof updateUserPermissionTimeRangeReqSchema>;

// Query Parameter Schemas

export const queryUserPermissionQuerySchema = z.object({
  includeInactiveFlag: z.boolean().nullish()
});
export type QueryUserPermissionQuery = z.infer<typeof queryUserPermissionQuerySchema>;

export const queryGroupAssignedUserListQuerySchema = z.object({
  keyword: z.string().nullish(),
  practitionerTypeCodes: z.array(z.string()).nullish(),
  includeInactiveFlag: z.boolean().nullish()
});
export type QueryGroupAssignedUserListQuery = z.infer<typeof queryGroupAssignedUserListQuerySchema>;

export const queryEffectivePermissionQuerySchema = z.object({
  includeInactiveFlag: z.boolean().nullish()
});
export type QueryEffectivePermissionQuery = z.infer<typeof queryEffectivePermissionQuerySchema>;

export const queryAvailablePermissionListQuerySchema = z.object({
  keyword: z.string().nullish()
});
export type QueryAvailablePermissionListQuery = z.infer<typeof queryAvailablePermissionListQuerySchema>;

export const queryUserListByPermissionGroupQuerySchema = z.object({
  funcGroupId: z.string(),
  keyword: z.string().nullish(),
  userStatus: z.string().nullish(),
  practitionerTypeCodes: z.array(z.string()).nullish(),
  lastLoginDatetimeFrom: z.string().nullish(),
  lastLoginDatetimeTo: z.string().nullish(),
  sortField: z.string().nullish(),
  sortDirection: z.string().nullish(),
  page: z.number().nullish(),
  size: z.number().nullish()
});
export type QueryUserListByPermissionGroupQuery = z.infer<typeof queryUserListByPermissionGroupQuerySchema>;

export const queryPermissionGroupListQuerySchema = z.object({
  keyword: z.string().nullish(),
  activeFlag: z.boolean().nullish()
});
export type QueryPermissionGroupListQuery = z.infer<typeof queryPermissionGroupListQuerySchema>;

export const queryQuerySchema = z.object({
  system: z.string(),
  tableName: z.string(),
  pageNum: z.number(),
  pageSize: z.number(),
  filters: z.string().nullish(),
  likes: z.string().nullish(),
  sorts: z.string().nullish()
});
export type QueryQuery = z.infer<typeof queryQuerySchema>;

export const queryStructureQuerySchema = z.object({
  system: z.string(),
  tableName: z.string()
});
export type QueryStructureQuery = z.infer<typeof queryStructureQuerySchema>;
